import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { showRewardedAd } from "../lib/ads";

/**
 * Shown when the user tries to play but doesn't have enough chips for the
 * ante. Three escape paths:
 *   - Watch a rewarded ad (+50 chips, daily-capped)
 *   - Open the gem store (premium IAP — biggest conversion moment)
 *   - Cancel / go back
 * Backend daily-cap (chips_50: 5/day) gracefully degrades the ad path.
 */
export default function OutOfChipsModal({
  open,
  required,
  current,
  onClose,
  onCredited,        // (chips) => void
  onOpenStore,       // () => void
}) {
  const [busy, setBusy] = useState(false);
  const [adErr, setAdErr] = useState("");
  const [adDone, setAdDone] = useState(false);

  const need = Math.max(0, required - current);

  const watch = async () => {
    if (busy) return;
    setBusy(true); setAdErr("");
    try {
      const res = await showRewardedAd("chips_rescue_100");
      onCredited?.(res.chips || 100);
      setAdDone(true);
      setTimeout(() => { setAdDone(false); onClose?.(); }, 1200);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail
        || (status === 429 ? "Rescue ad on cooldown — try the store!" : e?.message || "Ad unavailable.");
      setAdErr(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.82)" }}
          data-testid="out-of-chips-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-sm w-full p-7 rounded-3xl text-center relative"
            style={{ maxHeight: "92vh", overflowY: "auto" }}
            style={{ borderColor: "rgba(255,92,92,0.55)", boxShadow: "0 0 40px rgba(255,92,92,0.25)" }}
          >
            <div className="label-eyebrow mb-2" style={{ color: "#FF6B6B", letterSpacing: 2 }}>
              OUT OF CHIPS
            </div>
            <h2 className="font-display font-black text-2xl mb-2">Need more chips?</h2>
            <p className="text-white/65 text-sm mb-5">
              You have <b style={{ color: "#FFD700" }}>{current}</b>, this game needs{" "}
              <b style={{ color: "#FFD700" }}>{required}</b>. Short by{" "}
              <b style={{ color: "#FF8A8A" }}>{need}</b>.
            </p>

            {adDone ? (
              <div
                className="font-display font-bold text-lg mb-3"
                style={{ color: "#3DDC97" }}
                data-testid="out-of-chips-ad-success"
              >+100 chips credited!</div>
            ) : (
              <div className="space-y-3">
                <button
                  className="w-full rounded-full py-3 font-display font-bold text-sm flex items-center justify-center gap-2 transition-all"
                  onClick={watch}
                  disabled={busy}
                  style={{
                    background: "linear-gradient(135deg, rgba(61,220,151,0.20), rgba(0,229,255,0.18))",
                    border: "1.5px solid rgba(61,220,151,0.55)",
                    boxShadow: busy ? "none" : "0 0 18px rgba(61,220,151,0.28)",
                    opacity: busy ? 0.6 : 1,
                  }}
                  data-testid="out-of-chips-watch-ad"
                >
                  <span>📺</span> {busy ? "Loading…" : "Watch ad · +100 chips (rescue)"}
                </button>

                <button
                  className="w-full rounded-full py-3 font-display font-bold text-sm flex items-center justify-center gap-2"
                  onClick={() => { onClose?.(); onOpenStore?.(); }}
                  style={{
                    background: "linear-gradient(135deg,#FFD700,#FF8C00)",
                    color: "#0B0B0B",
                    boxShadow: "0 0 22px rgba(255,215,0,0.45)",
                  }}
                  data-testid="out-of-chips-go-store"
                >
                  <span>◆</span> Get a gem pack
                </button>

                <button
                  className="text-white/45 text-xs hover:text-white/75 mt-2"
                  onClick={onClose}
                  data-testid="out-of-chips-cancel"
                >Maybe later</button>
              </div>
            )}

            {adErr && (
              <div
                className="mt-3 text-xs"
                style={{ color: "#FF8A8A" }}
                data-testid="out-of-chips-error"
              >{adErr}</div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
